import { useQuery } from '@apollo/client'
import { Search as SearchIcon } from '@mui/icons-material'
import { MergeType } from '@mui/icons-material'
import { Link } from 'react-router-dom'
import { Button, Stack, TextField, Typography } from '@mui/material'
import { type GetCompaniesQuery } from '__generated__/graphql'
import { debounce } from 'lodash'
import {
    useCallback,
    useEffect,
    useRef,
    useState,
    type ChangeEvent,
    type FC,
} from 'react'
import { GetCompanies } from 'recruiter/graphql/companies.gql'
import { recruiterRouteConfig } from 'recruiter/routes'
import { usePageTitle } from 'shared/hooks/usePageTitle'
import { LoadingPanel } from 'shared/components/LoadingPanel'
import useToast from 'shared/components/Toast/useToast'
import useIsMobile from 'shared/hooks/useIsMobile'
import { getPxAsRem } from 'shared/utils'
import CompanyCard from './CompanyCard'
import EditProfile from './EditProfile'

const Companies: FC = () => {
    usePageTitle('Companies')
    const [companies, setCompanies] = useState<
        GetCompaniesQuery['companiesByName']['data']
    >([])
    const [pageTotal, setPageTotal] = useState(0)
    const currentPageRef = useRef(1)
    const [isFetching, setIsFetching] = useState(false)
    const [edit, setEdit] = useState(false)
    const isMobile = useIsMobile()
    const toast = useToast()
    const debounceDelay = 300
    const observer = useRef<IntersectionObserver | null>(null)
    const defaultPageLimit = 15

    const { loading, error, fetchMore } = useQuery(GetCompanies, {
        fetchPolicy: 'network-only',
        variables: {
            page: 1,
            limit: defaultPageLimit,
            name: '',
        },
        onCompleted: (data) => {
            console.log(data.companiesByName.pageInfo)
            setCompanies(data.companiesByName.data)
            setPageTotal(data.companiesByName.pageInfo.pageTotal)
        },
        onError: (error) => {
            toast({
                title: 'Error',
                message: 'Unable to fetch companies, please try again',
                severity: 'error',
            })
            console.error(error)
        },
    })

    const refreshCompanies = () => {
        fetchMore({
            variables: {
                page: 1,
                limit: defaultPageLimit,
            },
            updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult) return prev
                setCompanies(fetchMoreResult.companiesByName.data)
                setPageTotal(fetchMoreResult.companiesByName.pageInfo.pageTotal)
                return fetchMoreResult
            },
        })
    }

    const debouncedSearch = useRef(
        debounce((inputValue: string) => {
            const trimmedValue = inputValue.trim()
            const variables = {
                page: 1,
                limit: defaultPageLimit,
                ...(trimmedValue && {
                    name: trimmedValue,
                }),
            }

            fetchMore({
                variables,
                updateQuery: (prev, { fetchMoreResult }) => {
                    if (!fetchMoreResult) return prev
                    setCompanies(fetchMoreResult.companiesByName.data)
                    setPageTotal(
                        fetchMoreResult.companiesByName.pageInfo.pageTotal
                    )
                    return fetchMoreResult
                },
            })
        }, debounceDelay)
    ).current

    const loadMoreCompanies = useCallback(() => {
        if (loading || isFetching || currentPageRef.current >= pageTotal) return
        setIsFetching(true)
        const nextPage = currentPageRef.current + 1

        fetchMore({
            variables: {
                page: nextPage,
                limit: defaultPageLimit,
            },
            updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult) return prev

                currentPageRef.current = nextPage
                setIsFetching(false)
                setCompanies((prevData) => [
                    ...prevData,
                    ...fetchMoreResult.companiesByName.data,
                ])
                setPageTotal(fetchMoreResult.companiesByName.pageInfo.pageTotal)
                return fetchMoreResult
            },
        })
    }, [fetchMore, isFetching, loading, pageTotal])

    useEffect(() => {
        if (observer.current) observer.current.disconnect()

        observer.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                loadMoreCompanies()
            }
        })

        return () => {
            if (observer.current) observer.current.disconnect()
        }
    }, [loadMoreCompanies])

    useEffect(() => {
        if (companies.length === 0) return
        const lastCompanyElement = document.querySelector(
            `.company-card:last-child`
        )
        if (lastCompanyElement) {
            observer.current?.observe(lastCompanyElement)
        }
        return () => {
            if (lastCompanyElement) {
                observer.current?.unobserve(lastCompanyElement)
            }
        }
    }, [companies])

    const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
        debouncedSearch(e.target.value.trim())
    }

    if (loading && currentPageRef.current === 1) return <LoadingPanel />

    if (error) return <Typography>Error loading companies.</Typography>

    return (
        <>
            <EditProfile
                open={edit}
                onClose={() => setEdit(false)}
                data={undefined}
                refetch={refreshCompanies}
            />

            <Stack
                padding={getPxAsRem(isMobile ? 16 : 24)}
                gap={getPxAsRem(isMobile ? 16 : 24)}
            >
                <Stack
                    direction={{ md: 'row' }}
                    alignItems={{ md: 'center' }}
                    gap={getPxAsRem(16)}
                >
                    <Typography variant={isMobile ? 'h3' : 'h2'}>
                        Companies
                    </Typography>

                    <TextField
                        label="Search"
                        onChange={handleSearchChange}
                        placeholder="Company name"
                        sx={{ marginLeft: { md: 'auto' } }}
                        InputProps={{
                            startAdornment: <SearchIcon color="action" />,
                        }}
                    />

                    {!isMobile && (
                        <>
                            <Button
                                variant="outlined"
                                startIcon={<MergeType />}
                                component={Link}
                                to={recruiterRouteConfig.mergeCompanies.path}
                            >
                                Merge Companies
                            </Button>
                            <Button onClick={() => setEdit(true)}>
                                Add Company
                            </Button>
                        </>
                    )}
                </Stack>

                <Stack gap={{ xs: getPxAsRem(8), md: getPxAsRem(16) }}>
                    {companies.length > 0 ? (
                        companies.map((client) => (
                            <div className="company-card" key={client.id}>
                                <CompanyCard client={client} />
                            </div>
                        ))
                    ) : (
                        <Typography alignSelf="center">
                            No companies found.
                        </Typography>
                    )}
                </Stack>

                {isFetching && <Typography>Loading more...</Typography>}
            </Stack>
        </>
    )
}

export default Companies
